import { Component, OnInit, Input, forwardRef, Output, EventEmitter, AfterViewInit, ChangeDetectorRef, AfterViewChecked } from '@angular/core';
import { ContractLength } from '../../models/contract-length';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { defaultContractLengths } from '@app/shared/models/default-contract-lengths';
import { SetContractLengthsSelected } from '@utility-information/state/actions/utility-information.actions';
import { sweetSpotCheckBoxStateSelector, sweetSpotWasRequestedSelector, contractLengthsWithSelectedSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { isReadOnlySelector } from '@app/state/app.selectors';

@Component({
  selector: 'app-contract-lengths',
  templateUrl: './contract-lengths.component.html',
  styleUrls: ['./contract-lengths.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContractLengthsComponent),
      multi: true,
    },
  ],
})
export class ContractLengthsComponent implements OnInit, ControlValueAccessor {
  @Input() readonly: boolean;
  @Input() showAllButton: boolean;
  @Input() showPlusButton: boolean;
  @Input() contractLenghtsToShow: number[]; 
  @Input() FilterMode: boolean; // does the control only affect UI elements (saveable data will not be changed)
  @Output() maxCountReached = new EventEmitter<boolean>();
  @Output() contractLengthClicked = new EventEmitter<ContractLengthClickEvent>();
  @Output() notifyAllChanged = new EventEmitter<boolean>();

  public contractLengths: ContractLength[];
  public customContractLengths: ContractLength[] = [];
  contractLengthButtonsToShow$: Observable<ContractLength[]>;
  quoteRequestId: string;
  contractLengthsAll$: Observable<boolean>;
  sweetSpotCheckBoxState$: Observable<boolean>;
  sweetSpotWasRequested$: Observable<boolean>;
  public get allContractLengths() {
    return this.contractLengths.concat(this.customContractLengths);
  }
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  allSelected: boolean = true;
  showMessage: boolean;
  route$: Subscription;
  message: string;
  isReadOnly$: Subscription;
  isReadOnly: boolean;

  constructor(
    private store: Store<AppState>, private cdRef: ChangeDetectorRef) {
      if(!this.FilterMode) {
        this.sweetSpotCheckBoxState$ = this.store.select(sweetSpotCheckBoxStateSelector);
        this.sweetSpotWasRequested$ = this.store.select(sweetSpotWasRequestedSelector);
      }
  }

  ngOnInit() {
    this.contractLengths = this.contractLenghtsToShow?.map(btnValue => ({ months: btnValue, selected: true }) as ContractLength);
    this.contractLengthButtonsToShow$ = this.store.select(contractLengthsWithSelectedSelector)
    .pipe(
      distinctUntilChanged(),
      switchMap((xContrLenghts) => {
        if (xContrLenghts?.length > 0) { this.contractLengths = xContrLenghts; }
        setTimeout(() => { this.changeFormData(); });
        return of(xContrLenghts && xContrLenghts.length > 1 
          ? [...xContrLenghts].sort((a: ContractLength, b: ContractLength) => (a.months > b.months) ? 1 : -1)
          : null);
      })
    );

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });
  }

  ngOnDestroy() {
    this.route$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  lengthClicked(cLength: ContractLength) {
    this.contractLengths = this.contractLengths.map((cl) => ({months: cl.months, selected: cl.months === cLength.months ? !cLength.selected : cl.selected })) as ContractLength[];
    this.allSelected = this.contractLengths.filter((cl) => cl.selected == false)?.length == 0;
    this.dispatchContractLengthsSelected();
  }

  allClicked() {
    this.allSelected = !this.allSelected;
    this.notifyAllChanged.emit(this.allSelected);
    this.contractLengths = this.contractLengths.map((cl) => ({months: cl.months, selected: this.allSelected})) as ContractLength[];
    this.dispatchContractLengthsSelected();
  }

  dispatchContractLengthsSelected() {
    this.store.dispatch(new SetContractLengthsSelected(this.quoteRequestId, this.contractLengths));
    if(this.FilterMode) {      
      this.contractLengthClicked.emit({
        selected: this.allContractLengths.filter((c) => c.selected == true).map(c => c.months),
        maxReached: false,
      });
    }
    this.changeFormData();
  }

  validateLenghtSelection() {
    if (this.allContractLengths.filter((cl) => cl.selected).length == 0) {
      this.message = 'Please select at least one contract length.';
      this.showMessage = true;
    } else {
      this.message = '';
      this.showMessage = false;
    }    
  }

  plusClicked(eventData) {
    this.customContractLengths.push({ months: eventData.target.value, selected: false, custom: true} as ContractLength);    
  }

  closeCustom(cLength: ContractLength) {
    const index = this.customContractLengths.indexOf(cLength, 0);
    this.customContractLengths.splice(index, 1);
    this.changeFormData();
  }

  customFocusOut(eventData) {
    this.customContractLengths = this.customContractLengths.filter(x => x.selected === true);
    if (eventData.target.value > 0) {
      this.customContractLengths.push({ months: +eventData.target.value, selected: true } as ContractLength);
      this.changeFormData();
    }
  }

  
  changeFormData() {
    this.onChange(this.toTermsArray(this.allContractLengths.filter(x => x.selected === true)));
    this.validateLenghtSelection();
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  writeValue(value: number[]) {
    // highlight the selected months
    this.contractLengths = this.toDefaultContractLengthsArraySetSelected(value);
    this.customContractLengths = this.toCustomContractLengthsArraySetSelected(value);

    // if not all true all is not selected
    this.allSelected = this.contractLengths?.filter((cl) => cl.selected === false)?.length === 0;

    // if no selected contractlengths default to all selected
    if(this.contractLengths?.filter((x) => x.selected)?.length === 0) {
      this.contractLengths = this.toDefaultContractLengthsArraySetSelected(this.contractLenghtsToShow);
      this.allSelected = true;
    }
  }

  toDefaultContractLengthsArraySetSelected(terms: number[]) {
    if (terms?.length === undefined) terms = undefined;
    // if no terms provided use default terms and set selected to true
    return this.contractLenghtsToShow?.map((x) => ({ months: x, selected: terms?.includes(x) ?? true})) as ContractLength[];
  }

  toCustomContractLengthsArraySetSelected(terms: number[]) {
    return terms?.length > 0 ? terms?.filter(x => !this.contractLenghtsToShow.includes(x)).map(x => ({months: x, selected: true})) as ContractLength[] : [];
  }

  toTermsArray(contractLengths: ContractLength[]) {
    return contractLengths?.map((x) => x.months) ?? [];
  }
}
export class ContractLengthClickEvent {
  public selected: number[];
  public maxReached: boolean;
}

